import React from 'react';
import { Avatar, Box, IconButton, Typography } from '@mui/material';
import {
  AI_AGENT_ASSISTANT_NAME,
  AI_AGENT_HEADER_HEIGHT,
  AI_AGENT_TOKENS,
} from '../AIAgentConstants';
import { AIAgentMode } from '../AIAgentTypes';
import {
  AIAgentAvatarStarsIcon,
  AIAgentCloseIcon,
  AIAgentExpandIcon,
  AIAgentRefreshIcon,
} from './AIAgentIcons';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

interface Props {
  mode: AIAgentMode;
  onClose: () => void;
  onExpand: () => void;
  onReturnNormal: () => void;
  onStartNewConversation: () => void;
  disableControls: boolean;
}

/** Quadrado fixo 30×30; o glyph interno é menor (~14) para não colar nas bordas. */
const HEADER_ICON_BUTTON_SIZE = 30;
const HEADER_ICON_GLYPH_SIZE = 14;

const headerIconButtonSx = {
  width: HEADER_ICON_BUTTON_SIZE,
  height: HEADER_ICON_BUTTON_SIZE,
  p: 0,
  borderRadius: `${AI_AGENT_TOKENS.borderRadius}px`,
  backgroundColor: AI_AGENT_TOKENS.headerIconBg,
  color: AI_AGENT_TOKENS.white,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.28)',
  },
  '&.Mui-disabled': {
    backgroundColor: AI_AGENT_TOKENS.headerIconBg,
    opacity: 0.5,
  },
};

export default function AIAgentHeader({
  mode,
  onClose,
  onExpand,
  onReturnNormal,
  onStartNewConversation,
  disableControls,
}: Props) {
  return (
    <Box
      sx={{
        minHeight: AI_AGENT_HEADER_HEIGHT,
        px: 2,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: AI_AGENT_TOKENS.primary,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            backgroundColor: AI_AGENT_TOKENS.white,
          }}
        >
          <AIAgentAvatarStarsIcon size={20} />
        </Avatar>
        <Typography
          component="h2"
          sx={{
            fontFamily: AI_AGENT_TOKENS.fontFamilyDisplay,
            fontWeight: 500,
            fontSize: 20,
            lineHeight: 1.334,
            color: AI_AGENT_TOKENS.white,
          }}
        >
          {AI_AGENT_ASSISTANT_NAME}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {mode === 'expanded' ? (
          <IconButton
            size="small"
            aria-label="Retornar ao tamanho normal"
            onClick={onReturnNormal}
            disabled={disableControls}
            sx={headerIconButtonSx}
          >
            <CloseFullscreenIcon
              sx={{ fontSize: HEADER_ICON_GLYPH_SIZE, color: AI_AGENT_TOKENS.white }}
            />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            aria-label="Expandir painel"
            onClick={onExpand}
            disabled={disableControls}
            sx={headerIconButtonSx}
          >
            <AIAgentExpandIcon size={HEADER_ICON_GLYPH_SIZE} />
          </IconButton>
        )}

        <IconButton
          size="small"
          aria-label="Iniciar nova conversa"
          onClick={onStartNewConversation}
          disabled={disableControls}
          sx={headerIconButtonSx}
        >
          <AIAgentRefreshIcon size={HEADER_ICON_GLYPH_SIZE} />
        </IconButton>

        <IconButton
          size="small"
          aria-label="Fechar assistente"
          onClick={onClose}
          sx={headerIconButtonSx}
        >
          <AIAgentCloseIcon size={HEADER_ICON_GLYPH_SIZE} />
        </IconButton>
      </Box>
    </Box>
  );
}
